<script>
  // ============================================================
  // Original imports — preserved so the existing app keeps working.
  // ============================================================
  import { onMount, createEventDispatcher } from "svelte";
  import {
    User,
    Children,
    askPushPermission,
    savePushSubscription,
    isPushSupported
  } from "../../store.js";
  import Modal from "../../components/HTMLElements/Modal.svelte";
  import ModalConfirm from "../../components/HTMLElements/ModalConfirm.svelte";
  import VerifySMS from "../../components/SMSVerification.svelte";
  import wrappedFetch from "../../wrappedFetch.js";
  import Help from "../../components/help.svelte";
  import Feedback from "../../components/Feedback.svelte";
  import { dateToWords } from "../../components/application/store.js";

  export let emailvalidated;
  export let isLoading = true;
  export let showInstallHelp = false;

  // ============================================================
  // LOAD GATING
  // ----
  // `isLoading` is a prop, so the PARENT owns it. If the parent
  // passes or binds a value that's already false (e.g. left over
  // from a previous page), our `true` default never applies and the
  // page paints on the very first tick — with whatever the
  // module-scoped `Children` store still holds from the last visit.
  // That's the "delay, then the page changes" flash: old data paints,
  // the fetch resolves, everything re-renders with the real statuses.
  //
  // `loadState` is a LOCAL flag that only this component writes, so
  // nothing outside can make us render data we haven't fetched yet.
  // Nothing derived from the store is rendered until it's "ready".
  //
  //   "loading" — first fetch in flight; show the skeleton
  //   "ready"   — store holds data fetched by THIS component instance
  //   "error"   — fetch failed; show a recoverable error, not "no children"
  //
  // `isRefreshing` covers background refetches (after a delete) where
  // we already have good data on screen and blanking the page would be
  // worse than briefly showing the pre-delete list.
  // ============================================================
  let loadState = "loading";
  let isRefreshing = false;
  $: ready = loadState === "ready";

  // ============================================================
  // STATE METADATA
  // The five application statuses supported by eAdmissions, plus a UI
  // sub-state ("new") for a Not submitted application that the parent
  // hasn't yet given a phase / Local Authority. The DB only knows the
  // five — `new` is presentational only and derived in the mapper.
  // ============================================================
  const STATES = {
    new:     { pill: "Not started",          tone: "new"    },
    notSub:  { pill: "Not submitted",        tone: "action" },
    sub:     { pill: "Submitted",            tone: "ok"     },
    edits:   { pill: "Unsubmitted edits",    tone: "action" },
    cancel:  { pill: "Cancelled",            tone: "closed" },
    deleted: { pill: "Deleted",              tone: "closed" }
  };

  // Offer sub-state overrides — applied on top of state="sub" when an
  // offer has been made. Replaces the pill text + card tone so the
  // parent sees "Offer accepted" / "Respond to offer" rather than a
  // generic "Submitted". Pill labels are kept short so the side column
  // can hold a consistent width across cards.
  const OFFER_STATES = {
    pending:   { pill: "Respond to offer",   tone: "action" },
    accepted:  { pill: "Offer accepted",     tone: "ok"     },
    declined:  { pill: "Offer declined",     tone: "closed" },
    responded: { pill: "Offer responded",    tone: "ok"     },
    // No school offered: amber card, and no status pill at all (the
    // "No offer at this time" alert carries the message instead).
    none:      { pill: "",                   tone: "action" },
    // Late application, outcome not loaded yet: blue "information" card,
    // no pill — the "Outcome not yet available" alert explains it.
    notReceived: { pill: "",                 tone: "info"   }
  };

  // ============================================================
  // ACTION BUTTON DEFINITIONS
  // Maps action keys (used in app.actions) to a label, an icon
  // name, and whether the button is the card's primary CTA.
  // ============================================================
  const ACTION_DEFS = {
    "start":         { label: "Start application",        primary: true,  icon: "arrow" },
    "add-schools":   { label: "Add Schools",              primary: true,  icon: "arrow" },
    "review-submit": { label: "Review and submit",        primary: true,  icon: "arrow" },
    "resubmit":      { label: "Re-submit application",    primary: true,  icon: "arrow" },
    "respond-offer": { label: "Respond to outcome",       primary: true,  icon: "arrow" },
    "view-app":      { label: "View application",         primary: false, icon: "eye" },
    "view-changes":  { label: "View changes",             primary: false, icon: "eye" },
    "view-outcome":  { label: "View full outcome",        primary: false, icon: "eye" },
    "edit-schools":  { label: "Edit selected schools",    primary: false, icon: "pencil" },
    "attach-docs":   { label: "Attach a document",        primary: false, icon: "paper" },
    "view-guidance": { label: "View guidance",            primary: false, icon: "info" },
    "start-new":     { label: "Start a new application",  primary: true,  icon: "arrow" },
    "delete":        { label: "Delete application",       primary: false, icon: "trash" },
    "delete-draft":  { label: "Delete application",       primary: false, icon: "trash" }
  };

  // ============================================================
  // ICON LIBRARY
  // SVG inner-paths only — the wrapping <svg class="icon"> is added
  // in the template. Same set as the mockup.
  // ============================================================
  const ICONS = {
    arrow:    '<path d="M5 12h14M13 6l6 6-6 6" />',
    doc:      '<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" />',
    paper:    '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M17 8l-5-5-5 5" /><path d="M12 3v12" />',
    pencil:   '<path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4z" />',
    trash:    '<path d="M3 6h18" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />',
    warn:     '<path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4M12 17h.01" />',
    check:    '<path d="M20 6 9 17l-5-5" />',
    eye:      '<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z"/><circle cx="12" cy="12" r="3"/>',
    sibling:  '<circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><circle cx="17" cy="7" r="3"/><path d="M22 21v-2a3 3 0 0 0-2-2.9"/>',
    calendar: '<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>',
    ban:      '<circle cx="12" cy="12" r="10"/><path d="m4.93 4.93 14.14 14.14"/>',
    info:     '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>',
    refresh:  '<path d="M21 12a9 9 0 1 1-2.64-6.36"/><path d="M21 3v6h-6"/>'
  };

  let additionalInformation = "";
  let childToDelete = null;
  let isChildDeleting = false;
  let applicationToDelete = null;
  let isApplicationDeleting = false;
  let info = "";

  // ============================================================
  // DERIVED STATE
  // ----
  // `displayChildren` is the single source of truth for the UI. It's
  // derived from the `$Children` store via `mapApiChildToDisplay`.
  // The template only renders this once `loadState === "ready"`, so a
  // stale store from a previous visit is never painted.
  // ============================================================
  $: displayChildren = ($Children || []).map(mapApiChildToDisplay);

  // ============================================================
  // SIBLING LINK INDEX
  // ----
  // For each child, work out whether they're referenced as a
  // sibling on someone else's application. If they are, removing
  // the child would orphan that sibling reference, so the Remove
  // button is disabled (with a tooltip explaining where the
  // reference lives so the parent knows what to edit).
  //
  // We walk every other child's schools list looking for matches
  // by full name. Sibling references in the data are name-based
  // (no sibling_child_id surfaces to the front end), so this is
  // the most reliable join we can do client-side.
  // ============================================================
  $: siblingLinks = (() => {
    const links = new Map();   // childId → [{ byChildName, schoolName }]
    for (const refChild of displayChildren) {
      for (const app of refChild.applications) {
        for (const school of (app.schools || [])) {
          if (!school.siblingName) continue;
          // Find any child on this account whose full name matches
          // the sibling label on this school.
          const matched = displayChildren.find(c =>
            c.id !== refChild.id &&
            c.name.toLowerCase() === school.siblingName.toLowerCase()
          );
          if (matched) {
            if (!links.has(matched.id)) links.set(matched.id, []);
            links.get(matched.id).push({
              byChildName: refChild.forename,
              schoolName:  school.name
            });
          }
        }
      }
    }
    return links;
  })();

  // ============================================================
  // SORTING — UI-only; doesn't touch the Children store or the API
  // payload. `sortedChildren` is the list the template renders;
  // changing `sortBy` re-derives it instantly. Preference persists
  // across visits via localStorage.
  // ============================================================
  const SORT_KEY = "ea-home-sort";
  let sortBy =
    (typeof localStorage !== "undefined" && localStorage.getItem(SORT_KEY)) ||
    "actionFirst";

  // Save the choice back as soon as it changes.
  $: if (typeof localStorage !== "undefined") {
    localStorage.setItem(SORT_KEY, sortBy);
  }

  // Lower number = higher priority. Children with apps needing action
  // sort to the top; within "submitted" status, each offer state gets
  // its own band so cards with the same outcome group together rather
  // than scatter alphabetically.
  //
  //   0  Offer pending          — needs the parent's response
  //   1  Unsubmitted edits      — needs the parent to re-submit
  //   2  Not yet submitted      — work in progress, not visible to LA
  //   3  Offer accepted         — success
  //   4  Offer responded        — replied but reply type unknown
  //   5  Offer declined         — terminal but parent-chosen
  //   6  Submitted, no offer    — waiting on the LA
  //   7  Not started            — calm placeholder
  //   8  Cancelled / deleted    — terminal, parent didn't choose
  function actionPriority(child) {
    const apps = child.applications || [];
    if (apps.some(a => a.offerStatus === "pending"))   return 0;
    if (apps.some(a => a.state === "edits"))           return 1;
    if (apps.some(a => a.state === "notSub"))          return 2;
    if (apps.some(a => a.offerStatus === "accepted"))  return 3;
    if (apps.some(a => a.offerStatus === "responded")) return 4;
    if (apps.some(a => a.offerStatus === "declined"))  return 5;
    if (apps.some(a => a.state === "sub"))             return 6;
    if (apps.some(a => a.state === "new"))             return 7;
    return 8;
  }

  function sortComparator(mode) {
    switch (mode) {
      case "name":
        return (a, b) => a.name.localeCompare(b.name);
      case "ageDesc":
        // Older children first — earlier DOB sorts first.
        return (a, b) => (a.dob || "").localeCompare(b.dob || "");
      case "ageAsc":
        return (a, b) => (b.dob || "").localeCompare(a.dob || "");
      case "actionFirst":
      default:
        // Primary: action priority. Tie-break: alphabetical so the
        // order is stable when two children share the same state.
        return (a, b) => {
          const diff = actionPriority(a) - actionPriority(b);
          return diff !== 0 ? diff : a.name.localeCompare(b.name);
        };
    }
  }

  // Spread before sort so we don't mutate displayChildren in place —
  // mutating would surprise other reactive consumers of the store.
  // For parents with 1–2 children the selector is hidden and we
  // always sort alphabetically — keeps the layout predictable for
  // the common case. The stored `sortBy` preference only takes
  // effect once a third child appears.
  //
  // Note this is another reason to gate on `loadState`: with an empty
  // or stale store `effectiveSort` resolves to "name", then flips to
  // the stored preference once real data lands — which reorders every
  // card on screen. Gating means the pre-flip order is never painted.
  $: effectiveSort  = displayChildren.length > 2 ? sortBy : "name";
  $: sortedChildren = [...displayChildren].sort(sortComparator(effectiveSort));

  $: allApps = displayChildren.flatMap(c =>
    c.applications.map(a => ({ ...a, child: c }))
  );
  // An application needs attention only if the parent can actually do
  // something about it — submit, re-submit, or respond to an offer. A
  // Not submitted / edited application past its closing date has no such
  // action, so it no longer appears in the banner.
  $: actionApps = allApps.filter(a =>
    a.actions.some(k => k === "review-submit" || k === "resubmit" || k === "respond-offer")
  );
  $: totalApps = allApps.length;

  // ============================================================
  // HELPERS
  // ============================================================
  function initials(name) {
    return name
      .split(" ")
      .map(s => s[0])
      .slice(0, 2)
      .join("");
  }

  function childHasSubmitted(child) {
    return child.applications.some(a =>
      ["sub", "edits"].includes(a.state)
    );
  }

  function actionVerb(app) {
    if (app.state === "notSub") return "submit";
    if (app.state === "edits")  return "re-submit changes for";
    if (app.state === "sub" && app.actions.includes("respond-offer"))
      return "respond to the offer for";
    return "review";
  }

  function appUrl(child, app, kind) {
    // URL patterns preserved from the original component so the buttons
    // route into the same back-end pages.
    const cid = child.id;
    const aid = app.id;
    switch (kind) {
      case "review-submit":
      case "resubmit":
        return `/application/submit/${cid}/${aid}`;
      case "add-schools":
      case "edit-schools":
        return `/application/preferences/${cid}/${aid}`;
      case "view-app":
      case "view-changes":
        return `/application/summary/${cid}/${aid}`;
      case "view-outcome":
      case "respond-offer":
        return `/viewoffer/${aid}`;
      case "attach-docs":
        return `/application/documents/${cid}/${aid}`;
      case "delete":
      case "delete-draft":
        return `/application/delete/${cid}/${aid}`;
      case "start":
      case "start-new":
        return `/application/childdetails/${cid}`;
      case "view-guidance":
        return "/help";
      default:
        return "#";
    }
  }

  // ============================================================
  // MAPPER: API rows → display children
  // ----
  // The /child/getchildren_v2 endpoint now returns the flat
  // ChildSummary_V2 shape — one row per (child × application), with
  // application columns sitting alongside child columns. Children
  // referenced as siblings on someone else's application are already
  // filtered out at the SQL level (NOT EXISTS on application_schools
  // .sibling_id), so the data here is always real prospective pupils.
  //
  // Pipeline:
  //   raw flat rows --groupApiRows--> nested {child, applications[]}
  //                 --mapApiChildToDisplay--> display children
  //
  // The redesigned UI is driven by ONE of FIVE states derived from the
  // status string: notSub | sub | edits | cancel | deleted
  // ============================================================

  /** Format a DateTime? returned as an ISO string. Returns null if empty. */
  function fmtDate(iso) {
    if (!iso) return null;
    try { return dateToWords(iso); }
    catch { return iso; }
  }

  /**
   * Group the flat ChildSummary_V2 rows by child id, attaching their
   * applications as a nested array. Field-name translation happens
   * here so the rest of the mapper sees a consistent shape.
   */
  function groupApiRows(rows) {
    const map = new Map();
    for (const row of rows || []) {
      if (!map.has(row.id)) {
        map.set(row.id, {
          id: row.id,
          forename: row.forename,
          middlename: row.middleName,
          surname: row.surname,
          dob: row.dob,
          dobStr: row.dobStr,
          gender: row.gender,
          postcode: row.postCode || row.postcode,
          // EHCP (Education, Health and Care Plan) fields. Surfaces a
          // red warning callout under the child header when the LA has
          // flagged this child as not eligible for the standard flow.
          isEHCPAllowed:      row.isEHCPAllowed      ?? row.IsEHCPAllowed,
          ehcpNotAllowedText: row.ehcpNotAllowedText ?? row.EHCPNotAllowedText,
          // Back-end flag: this child is referenced as a sibling on
          // at least one active application within the same parent's
          // account. Authoritative for disabling Remove child; the
          // client-side `siblingLinks` map is still used for the
          // display detail (which child, which school).
          isSibling:          !!(row.isSibling ?? row.IsSibling),
          applications: []
        });
      }
      // Dapper coerces a NULL `app_id` from the LEFT JOIN to 0 because
      // ChildSummary_V2.AppId is a non-nullable int. So "no application
      // for this child" arrives as either appId=null or appId=0.
      const hasRealApp = row.appId != null && row.appId !== 0;

      if (hasRealApp) {
        // The schools array could arrive under any of these names
        // depending on which C# property holds it (`Schools` vs the
        // legacy `Applications`) and which serialiser casing convention
        // the project uses (camelCase via System.Text.Json vs PascalCase
        // via Newtonsoft default). Pick the first one that's an actual
        // array — empty or populated — and fall back to [].
        const rawSchools =
          [row.schools, row.Schools, row.applications, row.Applications]
            .find(x => Array.isArray(x))
          ?? [];

        const schools = rawSchools.map(s => ({
          p: s.sequence,
          name: s.name,
          dcsfCode: s.dcsfCode,
          // School intake gender — was `Gender` on the preference row;
          // renamed to `IntakeGender` for clarity.
          schoolGender: s.intakeGender ?? s.gender,
          // Sibling on this preference. Names are now SiblingForename /
          // SiblingSurname (prefixed so it's obvious whose name it is).
          siblingForename: s.siblingForename ?? s.forename ?? null,
          siblingSurname:  s.siblingSurname  ?? s.surname  ?? null,
          siblingName: s.siblingName
            ?? ([s.siblingForename ?? s.forename, s.siblingSurname ?? s.surname]
                 .filter(Boolean).join(" ") || null),
          // The school the sibling ATTENDS — distinct from name (the school
          // being applied to). Differs legitimately for linked infant/junior
          // schools; the callout must not imply attendance at the wrong one.
          siblingSchool: s.siblingSchoolName ?? null
        }));

        map.get(row.id).applications.push({
          applicationId:      row.appId,
          applicationStatus:  row.applicationStatus ?? row.status,
          reference:          row.reference,
          phase:              row.phase,
          laId:               row.laId,
          // LA display name — renamed `Name` → `LaName` on the C# side
          // (the old name clashed visually with the child's name).
          laDisplayName:      row.laName ?? row.name,
          submitted:          row.submitted,
          submittedStr:       row.submittedStr,
          stop:               row.stop,
          stopStr:            row.stopStr,
          lateStop:           row.lateStop ?? row.late_stop,
          lateStopStr:        row.lateStopStr,
          possibleOfferStop:  row.possibleOfferStop,
          reveal:             row.reveal,
          revealStr:          row.revealStr,
          onlineAccept:       row.onlineAccept,
          // Application-level flags now live on ChildSummary_V2.
          isSubmitted:             !!row.isSubmitted,
          isResubmittable:         !!row.isResubmittable,
          isApplicationViewable:   !!row.isApplicationViewable,
          isApplicationDeleteable: !!row.isApplicationDeleteable,
          isSchoolEditable:        !!row.isSchoolEditable,
          canApplyAgain:           !!row.canApplyAgain,
          // Upload availability is a separate signal from the offer
          // gate — the LA can keep uploads open during the offer
          // window (e.g. for appeals evidence) even though school
          // editing stays locked.
          isUploadVisible:         !!row.isUploadVisible,
          // Number of documents already attached to this application
          // (excluding disregarded ones). Drives the label swap on the
          // Attach button: 0 → "Attach a document", 1+ → "Manage
          // documents (N)". Comes from the back end via a COUNT
          // subquery on application_documents.
          attachedDocumentCount:   row.attachedDocumentCount ?? row.AttachedDocumentCount ?? 0,
          // Offer-related fields. Read both camelCase and PascalCase to
          // cover serialisers that preserve C# casing (Newtonsoft) vs
          // those that lowercase (System.Text.Json default).
          isOfferVisible:   !!(row.isOfferVisible   ?? row.IsOfferVisible),
          // Late application, outcome shown but no offer loaded yet.
          offerNotReceived: !!(row.offerNotReceived ?? row.OfferNotReceived),
          offerReplied:        row.offerReplied     ?? row.OfferReplied,
          offerRepliedStr:     row.offerRepliedStr  ?? row.OfferRepliedStr,
          offerReplyStatus:    row.offerReplyStatus ?? row.OfferReplyStatus,
          // Free-text reason supplied by the parent when declining an
          // offer (or by the LA when refusing). Only meaningful for
          // declined offers; null otherwise.
          offerDeclinedReason: row.offerDeclinedReason ?? row.OfferDeclinedReason ?? null,
          offerSchool:         row.offerSchool      ?? row.OfferSchool
                            ?? row.offerName        ?? row.OfferName,
          schools
        });
      } else {
        // No application record yet. Push a PLACEHOLDER app so the
        // child still gets a card — rendered in the "new" white state
        // with a "Start new application" button. The appId=0 in URLs
        // matches the back-end convention for "create a new app".
        // Only push one placeholder per child even if the flat rows
        // somehow produce duplicates.
        const childObj = map.get(row.id);
        if (childObj.applications.length === 0) {
          childObj.applications.push({
            applicationId:      0,
            applicationStatus:  null,
            reference:          null,
            phase:              null,
            laId:               null,
            laDisplayName:      null,
            submitted:          null,
            submittedStr:       null,
            stop:               null,
            lateStop:           null,
            possibleOfferStop:  null,
            reveal:             null,
            onlineAccept:       null,
            isOfferVisible:     false,
            offerNotReceived:   false,
            offerReplied:       null,
            offerReplyStatus:   null,
            offerDeclinedReason: null,
            isUploadVisible:    false,
            attachedDocumentCount: 0,
            schools:            []
          });
        }
      }
    }
    return Array.from(map.values());
  }

  /**
   * Append " Council" to an LA name if it doesn't already end with it.
   * The V2 SQL returns the bare `las.name` (e.g. "Croydon") whereas the
   * original `la_attributes` "Display Name" included it (e.g. "Croydon
   * Council"). This is a front-end nicety; longer term the back end
   * could return the proper display name directly.
   */
  function withCouncil(laName) {
    if (!laName) return "";
    return /council\s*$/i.test(laName) ? laName : `${laName} Council`;
  }

  function deriveAppState(apiApp) {
    const s = (apiApp.applicationStatus || "").toLowerCase().trim();

    // "Real" schools — filter out any phantom rows that might come back
    // with a null school_id (an empty preference slot, etc.). A school
    // only counts if it has a name we can actually display.
    const realSchools = (apiApp.schools || []).filter(x => x && x.name);
    const hasSchools = realSchools.length > 0;

    // Terminal / closed states always win — even an empty cancelled
    // application stays cancelled, not "new". The back end may report
    // this status as either "Cancelled" or "Disabled" (legacy naming).
    if (s === "deleted")                                          return "deleted";
    if (s === "cancelled" || s === "canceled" || s === "disabled") return "cancel";

    // Submitted variants are authoritative by status string.
    if (s === "submitted")                        return "sub";
    // Unsubmitted edits only count while they can still be re-submitted
    // (isResubmittable — before the closing date). After that the LA keeps
    // the last submitted version, so show it as plain Submitted, as V1 did.
    if (s === "submitted with unsubmitted edits") return apiApp.isResubmittable ? "edits" : "sub";

    // Not submitted — split by whether any actual work has been done.
    if (s === "not submitted") {
      return hasSchools ? "notSub" : "new";
    }

    // Status missing or unrecognised: if no real schools, treat as new.
    // This catches the case where an empty application record has been
    // created but the back end didn't surface the status string.
    if (!hasSchools) return "new";

    // Otherwise fallback to legacy boolean-based detection.
    if (apiApp.isResubmittable && apiApp.isSubmitted) return "edits";
    if (apiApp.isSubmitted)                           return "sub";
    return "notSub";
  }

  /**
   * Build the application title from whatever phase/LA we have.
   * Degrades cleanly when either or both are missing rather than
   * rendering a stub like "application ·".
   */
  function buildAppTitle(phase, la) {
    const laFull = withCouncil(la);
    if (phase && laFull) return `${phase} application · ${laFull}`;
    if (phase)           return `${phase} application`;
    if (laFull)          return `Application · ${laFull}`;
    return "New application";
  }

  function deriveAppActions(apiApp, state) {
    const actions = [];

    // Has the application been submitted at least once? The presence of
    // a reference number is the signal — references are assigned at
    // first submission and persist through cancel/delete.
    const hasReference = !!apiApp.reference;

    // Offer-respond overlay — only on a still-Submitted application
    // where the LA has made an offer. The respond button only shows
    // until the parent has replied, and never on a "no offer" outcome
    // (nothing to respond to — which also keeps it out of the "needs your
    // attention" banner). The view-outcome button is always available so
    // they can re-check the outcome details.
    if (state === "sub" && apiApp.isOfferVisible) {
      if (deriveOfferStatus(apiApp) === "pending") actions.push("respond-offer");
      actions.push("view-outcome");
    }

    // State-driven primary CTAs
    // Note: no primary CTA on `cancel` — the only meaningful action on
    // a cancelled application is deleting it (added further down).
    // Reapplying is done from the top-of-page "Start an application"
    // button, not from the cancelled card itself.
    // Gated on the back end's date window (isSchoolEditable — closing date,
    // or late closing date for a never-submitted application where the LA
    // accepts late ones). `edits` only exists while isResubmittable, see
    // deriveAppState. The appId=0 placeholder has no dates, so it always
    // gets its start button.
    if (state === "new") {
      if (apiApp.applicationId === 0 || apiApp.isSchoolEditable) actions.push("add-schools");
    }
    else if (state === "notSub") { if (apiApp.isSchoolEditable) actions.push("review-submit"); }
    else if (state === "edits")  actions.push("resubmit");

    // Secondary actions that require a submitted application
    // (i.e. one that has a reference number):
    //   View application — read-only summary, only meaningful once
    //     there's something to view.
    //   Attach a document — supporting docs can only be uploaded
    //     against an application the LA can already see. Normally
    //     locked once an offer is in play, but IsUploadVisible
    //     overrides this so the LA can re-open uploads during the
    //     offer/appeal window when extra evidence is needed.
    if (hasReference) {
      // View application — hidden on deleted (nothing to view) and on
      // cancel (the record's closed by the LA; the parent's next step
      // is delete, not review).
      if (state !== "deleted" && state !== "cancel") actions.push("view-app");
      if (state === "sub" || state === "edits") {
        const offerLocksUploads = apiApp.isOfferVisible && !apiApp.isUploadVisible;
        if (!offerLocksUploads) actions.push("attach-docs");
      }
    }

    // Edit selected schools — available while the application is still
    // open for changes. Editing a Submitted application is what triggers
    // the transition to "Submitted with unsubmitted edits". Locked once
    // an offer has been made: the parent must accept or decline the
    // existing offer rather than reshuffle their preferences. Also closed
    // once the change window has passed (isSchoolEditable from the back end).
    if ((state === "notSub" || state === "sub" || state === "edits") && !apiApp.isOfferVisible && apiApp.isSchoolEditable) {
      actions.push("edit-schools");
    }

    // Delete —
    //   `delete-draft` on a Not Submitted app: routes to the existing
    //     page-navigation flow at /application/delete/{cid}/{aid},
    //     which has its own confirmation + calls the internal
    //     ApplicationService.DeleteApplication server-side.
    //   `delete` on a Cancelled app: inline API call to the
    //     ParentDeleteApplication endpoint via a confirm modal on this
    //     page, no navigation.
    // The two branches exist because deleting a draft (with schools
    // and partial data) warrants a fuller confirmation than removing
    // a card that's already been closed by the LA.
    //
    // We check applicationId > 0 (not the derived state alone) because
    // an app with status "Not submitted" and no schools lands on the
    // UI-only "new" state — same state as the appId=0 placeholder
    // card for children with no app record at all. The placeholder
    // has nothing to delete; a real Not Submitted app does. The
    // applicationId gate distinguishes them cleanly.
    const isUnsubmittedRealApp =
      (state === "notSub" || state === "new") && apiApp.applicationId > 0;
    if (isUnsubmittedRealApp) {
      actions.push("delete-draft");
    }
    if (state === "cancel") {
      actions.push("delete");
    }

    return actions;
  }

  /**
   * Classify the offer sub-state for a submitted application. The DB
   * status string only knows {Submitted, Cancelled, …}; the offer
   * journey layers on top of it via three signals:
   *   IsOfferVisible    — LA has made an offer (or shown the outcome)
   *   OfferReplied      — DateTime when the parent responded; null = no reply
   *   OfferReplyStatus  — code or word indicating the reply.
   *     Accepted:  "A", "Y", "Yes", "Accepted"
   *     Declined:  "N", "D", "No", "Declined", "Refused"
   *
   * An outcome with no offered school (OfferSchool null and no reply) is
   * a "no offer" result — the LA has published the outcome but there is
   * nothing for the parent to accept or decline.
   *
   * OfferNotReceived (set by the back end) marks a late application whose
   * outcome is shown but which has no offer rows loaded yet — "not
   * received", distinct from a "no offer" result.
   *
   * Returns: 'pending' | 'none' | 'notReceived' | 'accepted' | 'declined' | 'responded' | null
   */
  function deriveOfferStatus(apiApp) {
    if (!apiApp.isOfferVisible) return null;
    if (apiApp.offerNotReceived) return "notReceived";
    const replied = !!(apiApp.offerRepliedStr || apiApp.offerReplied);
    if (!replied) return apiApp.offerSchool ? "pending" : "none";
    const s = (apiApp.offerReplyStatus || "").toString().toLowerCase().trim();
    if (s === "a" || s === "y" || s === "yes" || s === "accepted")
      return "accepted";
    if (s === "n" || s === "d" || s === "no" || s === "declined" || s === "refused")
      return "declined";
    return "responded"; // replied but reply code unrecognised
  }

  function mapApiApp(apiApp, apiChild) {
    const state = deriveAppState(apiApp);

    // Was the application submitted after the closing date? Compare
    // the ISO dates directly (string-comparison on ISO format yields
    // chronological order). Only meaningful for Submitted-ish states.
    const submittedLate = !!(
      apiApp.submitted &&
      apiApp.stop &&
      apiApp.submitted > apiApp.stop
    );

    return {
      id: apiApp.applicationId,
      state,
      submittedLate,
      title: buildAppTitle(apiApp.phase, apiApp.laDisplayName),
      phase: apiApp.phase || null,
      reference: apiApp.reference || null,
      submitted: apiApp.submittedStr || fmtDate(apiApp.submitted),
      // Closing date — formatted on the front end from `stop`. When the
      // C# layer adds a `StopStr` computed property we'll pick it up.
      closing:   apiApp.stopStr || fmtDate(apiApp.stop),
      lateClose: fmtDate(apiApp.lateStop),
      reveal:    apiApp.revealStr || fmtDate(apiApp.reveal),
      possibleOfferStop: fmtDate(apiApp.possibleOfferStop),
      // Optional fields not yet exposed by ChildSummary_V2:
      schools:      apiApp.schools || [],
      cancelledAt:  apiApp.cancelledAtStr || null,
      cancelReason: apiApp.cancelReason || null,
      deletedAt:    apiApp.deletedAtStr || null,
      edits:        apiApp.editsSavedStr ? { at: apiApp.editsSavedStr } : null,
      offerSchool:  apiApp.offerSchool || apiApp.offerName || apiApp.OfferName || null,
      respondBy:    apiApp.respondByStr || null,
      // OfferReplied is a DateTime?; format it for display. The boolean
      // form (has-replied) is what deriveAppActions checks.
      offerReplied:      apiApp.offerRepliedStr || fmtDate(apiApp.offerReplied),
      offerReplyStatus:  apiApp.offerReplyStatus || null,
      offerDeclinedReason: apiApp.offerDeclinedReason || null,
      offerStatus:       deriveOfferStatus(apiApp),
      attachedDocumentCount: apiApp.attachedDocumentCount || 0,
      actions: deriveAppActions(apiApp, state),
      deletable: !!apiApp.isApplicationDeleteable,
      deleteReason: apiApp.isApplicationDeleteable
        ? null
        : "Application is submitted. The Local Authority must cancel it before you can delete."
    };
  }

  function mapApiChildToDisplay(apiChild) {
    const fullName = [apiChild.forename, apiChild.middlename, apiChild.surname]
      .filter(Boolean)
      .join(" ");

    // Build the applications first so we can scan their schools.
    const applications = (apiChild.applications || []).map(a => mapApiApp(a, apiChild));

    // Collect every sibling reference across this child's applications —
    // one entry per (siblingName × school) pair, deduped so the same
    // sibling appearing on the same school via multiple apps doesn't
    // show twice. The single-vs-multiple distinction drives whether
    // the callout renders as a sentence or as a list.
    const siblings = [];
    const seenSibs = new Set();
    for (const app of applications) {
      for (const school of (app.schools || [])) {
        if (!school.siblingName) continue;
        const key = `${school.siblingName.toLowerCase()}::${(school.name || "").toLowerCase()}`;
        if (seenSibs.has(key)) continue;
        seenSibs.add(key);
        siblings.push({
          siblingName:   school.siblingName,
          school:        school.name || null,        // school being applied to
          siblingSchool: school.siblingSchool || null // school the sibling attends
        });
      }
    }

    return {
      id: apiChild.id,
      name: fullName,
      forename: apiChild.forename,
      sex: apiChild.gender,
      dob: apiChild.dobStr || (apiChild.dob ? dateToWords(apiChild.dob) : ""),
      postcode: apiChild.postcode || "",
      ehcpNotAllowed:
        apiChild.isEHCPAllowed === "N" &&
        !!(apiChild.ehcpNotAllowedText && apiChild.ehcpNotAllowedText.trim()),
      ehcpText: apiChild.ehcpNotAllowedText || "",
      // Back-end signal: this child is referenced as a sibling somewhere
      // on the account. Authoritative — Remove button is disabled when
      // true even if client-side name matching can't find the reference.
      isSibling: !!apiChild.isSibling,
      siblings,
      applications
    };
  }

  // ============================================================
  // FETCH + MOUNT
  // ============================================================
  const getChildren = async () => {
    const res = await wrappedFetch("/child/getchildren_v2?x=" + new Date().getTime());
    const result = await res.json();
    additionalInformation = result.additionalInformation;
    return result.children;
  };

  const dispatch = createEventDispatcher();

  /**
   * Single entry point for loading children. Both the initial mount and
   * the post-delete refetches go through here so the load-state
   * bookkeeping lives in one place.
   *
   * background = false (default) → first load. Flips to "loading", which
   *   swaps the list for the skeleton, so nothing stale is on screen.
   * background = true → refetch after a delete. Leaves `loadState` at
   *   "ready" and raises `isRefreshing` instead: we already have good
   *   data up, and blanking the whole list for a one-row change reads
   *   as a page reload.
   *
   * Returns true on success so callers can react to a failed refetch.
   */
  const loadChildren = async ({ background = false } = {}) => {
    if (background) isRefreshing = true;
    else loadState = "loading";

    try {
      const rows = await getChildren();
      Children.set(groupApiRows(rows));
      loadState = "ready";
      return true;
    } catch (e) {
      console.warn("Failed to load children:", e);
      // Only a failed FIRST load takes over the page. A failed
      // background refetch keeps the existing list — the caller
      // notifies instead, so the parent isn't left staring at an
      // error over data that's still mostly correct.
      if (!background) loadState = "error";
      return false;
    } finally {
      if (background) isRefreshing = false;
    }
  };

  const retryLoad = () => loadChildren();

  onMount(async () => {
    // Pull children + additionalInformation from the API. The fetch
    // sets `additionalInformation` as a side effect (see getChildren).
    // The flat ChildSummary_V2 rows are grouped into nested children
    // and pushed into the Children store, which drives the reactive
    // `displayChildren` above.
    await loadChildren();
    isLoading = false;
    showInstallHelp = false;
    if ($User == null) {
      dispatch("pageChange", "/signin");
    }
  });

  // ============================================================
  // DELETE CHILD — preserved from the original, now gated on
  // whether the child has any submitted applications.
  // ============================================================
  const requestDeleteChild = child => {
    // Belt-and-braces; the button itself is hidden / disabled for these.
    if (childHasSubmitted(child)) return;
    if ((siblingLinks.get(child.id) || []).length > 0) return;
    // Schools selected on any application — even on a Not submitted
    // application — means meaningful work that shouldn't be discarded
    // via a single click. The parent must clear preferences first.
    const hasSchools = child.applications.some(app =>
      (app.schools || []).some(s => s && s.name)
    );
    if (hasSchools) return;
    childToDelete = child;
  };

  const deleteChild = () => {
    let form = new FormData();
    form.append("id", childToDelete.id);
    isChildDeleting = true;
    wrappedFetch(`/child/deletechilddetails`, { method: "post", body: form })
      .then(r => r.json())
      .then(async response => {
        if (response.success) {
          // Refetch in the background (same reasoning as
          // deleteApplication below) — the API is the single source of
          // truth for who's visible.
          const refreshed = await loadChildren({ background: true });
          childToDelete = null;
          dispatch("notification", "Child deleted successfully.");
          if (!refreshed) {
            dispatch(
              "notification",
              "The child was deleted. Refresh the page to see the updated list."
            );
          }
        } else {
          dispatch("notification", response.message);
          dispatch("pageChange", `/user/home`);
        }
        isChildDeleting = false;
      });
  };

  // ============================================================
  // DELETE APPLICATION — inline API call rather than a page redirect.
  // Same pattern as deleteChild above. Only offered on cancelled
  // applications (deriveAppActions gates it), and the button is
  // further disabled unless app.deletable is true.
  // ============================================================
  const requestDeleteApplication = (child, app) => {
    // Belt-and-braces; the button is disabled when this is false.
    if (!app.deletable) return;
    applicationToDelete = { child, app };
  };

  const deleteApplication = () => {
    const { child, app } = applicationToDelete;
    const form = new FormData();
    // The application id uniquely identifies the record — no need to
    // send childId. The service call still has access to loggedUser
    // for the auth check (parent owns this app).
    form.append("id", app.id);
    isApplicationDeleting = true;
    wrappedFetch(`/child/ParentDeleteApplication`, { method: "post", body: form })
      .then(r => r.json())
      .then(async response => {
        if (response.success) {
          // Refetch rather than filter locally. The SQL WHERE clause
          // in ChildRepo.GetChildren_V2 has rules about which children
          // stay visible after their last app is gone (e.g. still
          // referenced as a sibling → visible; nothing linking to
          // them → hidden). Rather than duplicate that logic here,
          // just reload the same list the page-load fetch produces.
          const refreshed = await loadChildren({ background: true });
          applicationToDelete = null;
          dispatch("notification", "Application deleted successfully.");
          if (!refreshed) {
            dispatch(
              "notification",
              "The application was deleted. Refresh the page to see the updated list."
            );
          }
        } else {
          dispatch("notification", response.message || "Failed to delete application.");
        }
        isApplicationDeleting = false;
      });
  };

  // ============================================================
  // PUSH NOTIFICATIONS + SMS OPT-IN + FEEDBACK
  // Preserved from the original Home page — these are independent
  // concerns from the application card redesign but live on the
  // same screen, so the logic stays here.
  // ============================================================
  let isSMSOptingOut = false;
  let isSendingSMSVerificationCode = false;
  let showSMSOptIn = false;
  let showSMSOptinHelp = false;
  let showFeedback = false;
  let showMobileVerification = false;
  let askSMSVerificationCode = false;
  let showOptoutConfirmModal = false;
  let isOptingOut = false;
  let showPushNotificationAlert = isPushSupported;
  const cookies = (typeof document !== "undefined" ? document.cookie : "").split(";");

  // SMS opt-in surfaces only when the user has a mobile number that
  // hasn't yet been verified (validation pending or never started).
  $: showSMSOptIn =
    $User &&
    $User.mobile &&
    $User.mobile !== "" &&
    ($User.isMobileValidated == null || $User.isMobileValidated === "P");

  // Push notification banner suppresses itself once the browser has
  // made a final decision (granted / denied) or the user has dismissed
  // the prompt within the last 7 days (cookie set in onNoPushClick).
  $: showPushNotificationAlert =
    showPushNotificationAlert &&
    typeof Notification !== "undefined" &&
    Notification.permission !== "denied" &&
    Notification.permission !== "granted" &&
    !cookies.find(c => c.includes("push-alert"));

  // Feedback prompt surfaces once the parent has submitted at least
  // one application but hasn't yet given a star rating.
  $: showFeedback =
    $User && $User.submittedApplications > 0 && !$User.rating;

  const getAuthUser = async () => {
    const resp = await wrappedFetch("/user/GetAuthUser", { method: "post" });
    return await resp.json();
  };

  const optOutSMS = e => {
    if (e) e.preventDefault();
    isSMSOptingOut = true;
    wrappedFetch(`/user/SMSOptOut`, { method: "post" })
      .then(r => r.json())
      .then(response => {
        if (response) {
          showSMSOptIn = false;
          dispatch("notification", "Text message alerts opted out successfully.");
        }
        isSMSOptingOut = false;
      });
  };

  const onYesPushClick = e => {
    e.preventDefault();
    askPushPermission().then(async result => {
      if (result === "granted") {
        const save = await savePushSubscription();
        if (save.success) {
          showPushNotificationAlert = false;
          dispatch("notification", "Opt-in was successful!");
        } else {
          dispatch("notification", save.message);
        }
      } else if (result === "denied") {
        showPushNotificationAlert = false;
      }
    });
  };

  const onNoPushClick = e => {
    if (e) e.preventDefault();
    // Cookie-suppress the banner for 7 days.
    const d = new Date();
    d.setTime(d.getTime() + 7 * 24 * 60 * 60 * 1000);
    document.cookie =
      "push-alert=1" +
      d.getTime() +
      "; expires = " +
      d.toUTCString() +
      ";path=/";
    showPushNotificationAlert = false;
  };

  const optoutNotificationAlerts = () => {
    isOptingOut = true;
    if (showSMSOptIn) optOutSMS();
    if (showPushNotificationAlert) onNoPushClick();
    isOptingOut = false;
    showOptoutConfirmModal = false;
  };

  const sendSMSVerificationCode = e => {
    e.preventDefault();
    isSendingSMSVerificationCode = true;
    wrappedFetch("user/sendSMSverificationcode")
      .then(r => r.json())
      .then(async result => {
        if (result.success) {
          askSMSVerificationCode = false;
          showMobileVerification = true;
          $User.isMobileValidated = (await getAuthUser()).user.isMobileValidated;
        }
        isSendingSMSVerificationCode = false;
      });
  };
</script>

<style>
  /* ============================================================
     Global design tokens — same set as the standalone mockup.
     Wrapped in :global() so they cascade out of Svelte's scope.
     ============================================================ */
  :global(:root) {
    /* Wavenet brand */
    --wn-blue:           #006DCB;
    --wn-dark-blue:      #1D252C;
    --wn-tech-blue:      #3DB6C7;
    --wn-purple:         #854796;
    --wn-green:          #2FA283;
    --wn-pink:           #D50057;
    --wn-orange:         #FF5F00;
    --wn-yellow:         #F1DC00;
    --wn-tech-blue-aa:   #1F8A99;
    --wn-green-aa:       #1F8869;
    --wn-orange-aa:      #C04500;

    /* Neutrals + surfaces */
    --wn-ink:            #1D252C;
    --wn-white:          #FFFFFF;
    --wn-bg:             #FFFFFF;
    --wn-bg-tint:        #F6F8FA;
    --wn-line:           #E5E8EB;
    --wn-line-strong:    #CCD3DA;
    --fg-1:              #1D252C;
    --fg-2:              #5B6772;
    --fg-3:              #8A95A1;

    /* Council brand — matches the existing site colour */
    --ea-brand:          #70457a;
    --ea-brand-ink:      #5a3263;
    --ea-link:           #0B3F8C;

    /* Status surfaces */
    --st-new-bg:         #FFFFFF;
    --st-new-border:     #D6DCE3;
    --st-new-strip:      #8A95A1;
    --st-new-fg:         #3A4651;

    --st-action-bg:      #FFF6E0;
    --st-action-border:  #F2D785;
    --st-action-strip:   #B8860B;
    --st-action-fg:      #6B4A00;

    --st-ok-bg:          #E6F3EC;
    --st-ok-border:      #A8D6BC;
    --st-ok-strip:       #1F8869;
    --st-ok-fg:          #0F5A40;

    --st-closed-bg:      #EDF0F3;
    --st-closed-border:  #CCD3DA;
    --st-closed-strip:   #6B7682;
    --st-closed-fg:      #3A4651;

    --st-info-bg:        #E4F2FB;
    --st-info-border:    #A9D2EE;
    --st-info-strip:     #1F6FA3;
    --st-info-fg:        #103E68;

    /* Error surface — used by the load-failure panel. */
    --st-err-bg:         #FCE8EF;
    --st-err-border:     #F0B3C8;
    --st-err-fg:         #8C0033;

    /* Typography — inherit from the parent app so the page picks up
       whatever font family the rest of eAdmissions is already using. */
    --font-display: inherit;
    --font-body:    inherit;
    --font-mono:    ui-monospace, "SF Mono", Menlo, Consolas, monospace;

    --dur-fast:   120ms;
    --dur-med:    200ms;
    --ease-out:   cubic-bezier(.2,.7,.2,1);
  }

  /* ------------- Page shell ------------- */
  .ea-page {
    max-width: 1180px;
    margin: 0 auto;
    padding: 8px 28px 80px;
    color: var(--fg-1);
    font-family: var(--font-body);
    -webkit-font-smoothing: antialiased;
  }
  /* Page title uses Bulma's .title class with inline color/align overrides
     (matches the existing H1 styling site-wide). */

  /* ------------- Hero / start CTA ------------- */
  /* ------------- Notification opt-in banner ------------- */
  .ea-optin {
    position: relative;
    border: 1px solid var(--wn-line);
    border-radius: 12px;
    background: var(--wn-bg-soft, #f7f4fa);
    padding: 18px 22px 20px;
    margin: 0 0 22px;
  }
  .ea-optin__close {
    position: absolute;
    top: 10px;
    right: 12px;
    background: transparent;
    border: 0;
    font-size: 22px;
    line-height: 1;
    color: var(--fg-2);
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 6px;
  }
  .ea-optin__close:hover { background: rgba(0,0,0,.06); color: var(--fg-1); }
  .ea-optin__title {
    margin: 0 0 4px;
    font-size: 1.125rem;
    color: var(--ea-brand, #70457a);
    font-weight: 700;
  }
  .ea-optin__intro {
    margin: 0 0 14px;
    color: var(--fg-2);
    font-size: 0.9375rem;
  }
  .ea-optin__intro a { color: var(--ea-brand, #70457a); text-decoration: underline; }
  .ea-optin__cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 12px;
  }
  .ea-optin__card {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    background: #fff;
    border: 1px solid var(--wn-line);
    border-radius: 10px;
    padding: 14px 16px;
  }
  .ea-optin__icon {
    flex: 0 0 auto;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: rgba(112, 69, 122, .10);
    color: var(--ea-brand, #70457a);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
  }
  .ea-optin__body { flex: 1 1 auto; min-width: 0; }
  .ea-optin__heading { margin: 0 0 2px; color: var(--fg-1); }
  .ea-optin__copy    { margin: 0 0 10px; color: var(--fg-2); font-size: 0.9375rem; }
  .ea-optin__buttons { display: flex; gap: 8px; }

  .ea-hero { margin-bottom: 28px; }
  .ea-actions { display: flex; gap: 10px; flex-wrap: wrap; }

  .btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 40px;
    padding: 0 18px;
    border-radius: 6px;
    font-family: var(--font-body);
    font-weight: 600;
    font-size: 1rem;
    border: 1px solid transparent;
    cursor: pointer;
    text-decoration: none;
    transition: filter var(--dur-med) var(--ease-out), background-color var(--dur-med);
    white-space: nowrap;
  }
  .btn--primary   { background: var(--ea-brand); color: #fff; }
  .btn--primary:hover { filter: brightness(1.08); text-decoration: none; }
  .btn--secondary { background: #fff; color: var(--fg-1); border-color: var(--wn-line-strong); }
  .btn--secondary:hover { background: #FAFBFC; text-decoration: none; }
  .btn--ghost     { background: transparent; color: var(--ea-brand); padding: 0 6px; height: 32px; font-weight: 600; }
  .btn--ghost:hover { background: rgba(79,30,96,.06); text-decoration: none; }
  .btn--small     { height: 32px; font-size: 0.875rem; padding: 0 12px; }
  .btn[disabled]  { opacity: .55; cursor: not-allowed; pointer-events: none; }
  .btn .arrow     { font-size: 16px; line-height: 1; transform: translateY(-1px); }

  /* ------------- Action banner ------------- */
  .ea-action-banner {
    background: #FFF6E0;
    border: 1px solid #F2D785;
    border-radius: 10px;
    padding: 14px 18px;
    display: flex;
    gap: 14px;
    align-items: flex-start;
    margin-bottom: 22px;
  }
  .ea-action-banner__icon {
    width: 32px; height: 32px;
    border-radius: 50%;
    background: #B8860B;
    color: #fff;
    display: grid; place-items: center;
    flex-shrink: 0;
    font-weight: 700;
    font-family: var(--font-display);
    font-size: 18px;
  }
  .ea-action-banner__body { flex: 1; }
  .ea-action-banner__title { font-weight: 600; font-size: 1rem; color: #6B4A00; margin: 0 0 2px; }
  .ea-action-banner__msg   { font-size: 0.9375rem; color: #6B4A00; margin: 0; }
  .ea-action-banner__msg--footer { margin-top: 6px; }
  .ea-action-banner__list {
    margin: 4px 0 0;
    padding-left: 20px;
    list-style-type: disc;
    color: #6B4A00;
    font-size: 0.9375rem;
  }
  .ea-action-banner__list li { margin: 2px 0; line-height: 1.45; }

  /* ------------- Section title ------------- */
  .ea-section-title {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin: 28px 0 14px;
    gap: 12px;
    flex-wrap: wrap;
  }
  .ea-section-title h2 {
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 1.5rem;
    color: var(--fg-1);
    margin: 0;
    letter-spacing: -.01em;
  }
  .ea-section-meta { font-size: 0.875rem; color: var(--fg-3); }

  /* Right side of the section header: meta + sort selector */
  .ea-section-tools {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
  }
  .ea-sort {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
    color: var(--fg-2);
  }
  .ea-sort__label { white-space: nowrap; }
  .ea-sort__select {
    font-family: var(--font-body);
    font-size: 0.875rem;
    color: var(--fg-1);
    background: #fff;
    border: 1px solid var(--wn-line-strong);
    border-radius: 6px;
    padding: 4px 28px 4px 10px;
    height: 30px;
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' stroke='%235B6772' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/></svg>");
    background-repeat: no-repeat;
    background-position: right 10px center;
  }
  .ea-sort__select:focus {
    outline: none;
    border-color: var(--ea-brand);
    box-shadow: 0 0 0 3px rgba(112, 69, 122, .15);
  }

  /* Background-refresh marker — shown in the section header while a
     post-delete refetch is in flight. Deliberately quiet: the list on
     screen is still valid, this just explains why it might change. */
  .ea-refreshing {
    font-size: 0.8125rem;
    color: var(--fg-3);
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .ea-refreshing svg {
    width: 13px; height: 13px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    animation: ea-spin 900ms linear infinite;
  }
  @keyframes ea-spin { to { transform: rotate(360deg); } }

  /* Empty state — shown when the parent has no children yet. */
  .ea-empty {
    padding: 32px 24px;
    border: 1px dashed var(--wn-line-strong);
    border-radius: 12px;
    text-align: center;
    color: var(--fg-2);
    background: var(--wn-bg-tint);
  }
  .ea-empty p { margin: 0; }
  .ea-empty p + p { margin-top: 6px; }
  .ea-empty b { color: var(--fg-1); }

  /* ------------- Load failure panel -------------
     Distinct from .ea-empty on purpose. "You haven't added any
     children yet" over a failed fetch reads to a parent as though
     their applications have been deleted, which is the worst possible
     message to show on a network blip. This says what happened and
     offers the one action that fixes it. */
  .ea-error {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 20px 22px;
    border: 1px solid var(--st-err-border);
    border-left: 4px solid var(--wn-pink);
    border-radius: 12px;
    background: var(--st-err-bg);
    color: var(--st-err-fg);
  }
  .ea-error__icon {
    width: 32px; height: 32px;
    border-radius: 50%;
    background: var(--wn-pink);
    color: #fff;
    display: grid; place-items: center;
    flex-shrink: 0;
  }
  .ea-error__icon svg { width: 17px; height: 17px; }
  .ea-error__body { flex: 1; min-width: 0; }
  .ea-error__title { margin: 0 0 2px; font-weight: 600; font-size: 1rem; }
  .ea-error__msg   { margin: 0 0 12px; font-size: 0.9375rem; line-height: 1.45; }

  /* ------------- Loading skeleton -------------
     Mirrors the real layout (child header + one card per child) so the
     content fills in rather than pushing the page around. */
  .ea-skeleton { display: flex; flex-direction: column; gap: 28px; }
  .ea-skeleton__child { display: flex; flex-direction: column; gap: 12px; }
  .ea-skeleton__header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 4px 4px 14px;
    border-bottom: 1px dashed var(--wn-line);
  }
  .ea-skeleton__header-text { display: flex; flex-direction: column; gap: 8px; flex: 1; }
  .ea-skeleton__card {
    border: 1px solid var(--wn-line);
    border-radius: 12px;
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .ea-skeleton__row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .ea-bone {
    background: linear-gradient(90deg, #EDF0F3 25%, #F7F9FB 37%, #EDF0F3 63%);
    background-size: 400% 100%;
    animation: ea-shimmer 1.4s ease-in-out infinite;
    border-radius: 6px;
    flex-shrink: 0;
  }
  @keyframes ea-shimmer {
    0%   { background-position: 100% 50%; }
    100% { background-position: 0 50%; }
  }
  @media (prefers-reduced-motion: reduce) {
    .ea-bone { animation: none; }
    .ea-refreshing svg { animation: none; }
  }

  /* ------------- Child group ------------- */
  .ea-child {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 28px;
  }
  .ea-child__header {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 4px 4px 14px;
    border-bottom: 1px dashed var(--wn-line);
  }
  .ea-child__avatar {
    width: 42px; height: 42px;
    border-radius: 8px;
    background: linear-gradient(135deg, #EDE3F1, #F7F1F9);
    display: grid; place-items: center;
    font-family: var(--font-display);
    font-weight: 500;
    font-size: 16px;
    color: var(--ea-brand-ink);
    flex-shrink: 0;
  }
  .ea-child__main { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
  .ea-child__name {
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 1.25rem;
    color: var(--fg-1);
    letter-spacing: -.01em;
    line-height: 1.2;
    display: flex; align-items: center; gap: 10px;
    flex-wrap: wrap;
  }
  .ea-child__name-sex {
    font-weight: 400;
    font-size: 0.9375rem;
    color: var(--fg-3);
    font-family: var(--font-body);
  }
  .ea-child__meta {
    font-size: 0.9375rem;
    color: var(--fg-2);
    display: flex; gap: 14px; flex-wrap: wrap;
  }
  .ea-child__meta span::before { content: "·"; margin-right: 14px; color: var(--fg-3); }
  .ea-child__meta span:first-child::before { display: none; }
  .ea-child__actions { display: flex; gap: 4px; margin-top: 4px; }

  /* EHCP not-allowed warning — red callout shown under the child
     header when the LA has flagged this child as ineligible for the
     standard application flow. Surfaces the LA-supplied message. */
  .ehcp-warning {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-top: 10px;
    padding: 10px 12px;
    background: rgba(213, 0, 87, .08);
    border: 1px solid rgba(213, 0, 87, .35);
    border-left: 3px solid var(--wn-pink, #D50057);
    border-radius: 6px;
    color: #8C0033;
    font-size: 13px;
    line-height: 1.45;
  }
  .ehcp-warning__icon {
    width: 22px; height: 22px;
    border-radius: 50%;
    background: var(--wn-pink, #D50057);
    color: #fff;
    display: grid; place-items: center;
    flex-shrink: 0;
    margin-top: 1px;
  }
  .ehcp-warning__icon svg { width: 13px; height: 13px; }
  .ehcp-warning__text { font-weight: 500; }

  /* Sibling callout */
  .sibling-callout {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
    padding: 8px 12px;
    background: rgba(133,71,150,.08);
    border: 1px solid rgba(133,71,150,.25);
    border-left: 3px solid var(--wn-purple);
    border-radius: 6px;
    color: #4F1E60;
    font-size: 13px;
    line-height: 1.4;
  }
  .sibling-callout__icon {
    width: 26px; height: 26px;
    border-radius: 50%;
    background: var(--wn-purple);
    color: #fff;
    display: grid; place-items: center;
    flex-shrink: 0;
  }
  .sibling-callout__icon svg { width: 14px; height: 14px; }
  .sibling-callout__text b { font-weight: 600; color: #2C0F38; }
  .sibling-list {
    margin: 6px 0 4px 0;
    padding-left: 22px;
    list-style-type: disc;
  }
  .sibling-list li { margin: 2px 0; line-height: 1.4; }

  /* Incoming variant — appears on a child who is themselves referenced
     as a sibling on someone else's application. Dashed border instead
     of solid so a parent who has both callouts can tell them apart at
     a glance: solid = "this child has a sibling at X" (informational),
     dashed = "this child IS the sibling on Y" (actionable — remove
     this reference to unblock deletion). */
  .sibling-callout.is-incoming {
    border-style: dashed;
    background: rgba(133,71,150,.05);
  }

  /* Inline "sibling here" tag on a school row */
  .sibling-tag {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: 999px;
    background: var(--wn-purple);
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
  }
  .sibling-tag svg { width: 11px; height: 11px; }

  /* ------------- Application card ------------- */
  .ea-app {
    border: 1px solid var(--wn-line);
    border-radius: 12px;
    padding: 18px 20px;
    display: grid;
    grid-template-columns: 4px 1fr 220px;
    gap: 16px;
    align-items: stretch;
    position: relative;
    overflow: hidden;
  }
  .ea-app__strip { border-radius: 4px; background: var(--st-new-strip); }
  .ea-app__body  { display: flex; flex-direction: column; gap: 10px; min-width: 0; }
  .ea-app__head  { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
  .ea-app__title {
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 1.125rem;
    color: var(--fg-1);
    letter-spacing: -.01em;
    margin: 0;
  }
  .ea-app__sub {
    font-size: 0.9375rem;
    color: var(--fg-2);
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
  }
  .ea-app__sub span::before { content: "·"; margin-right: 14px; color: var(--fg-3); }
  .ea-app__sub span:first-child::before { display: none; }

  .ea-app__schools {
    display: flex;
    flex-direction: column;
    gap: 6px;
    background: rgba(255,255,255,.55);
    border: 1px solid rgba(0,0,0,.04);
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 0.9375rem;
  }
  .ea-app__schools-row { display: flex; align-items: center; gap: 10px; }
  .ea-app__schools-pref {
    display: inline-grid;
    place-items: center;
    width: 20px; height: 20px;
    border-radius: 50%;
    background: rgba(0,0,0,.08);
    font-size: 11px;
    font-weight: 700;
    color: var(--fg-1);
    flex-shrink: 0;
  }
  .ea-app__schools-name { color: var(--fg-1); }
  .ea-app__schools-meta { color: var(--fg-3); font-size: 12px; }
  .ea-app__schools-gender {
    display: inline-flex;
    align-items: center;
    padding: 2px 7px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    color: var(--fg-2);
    background: rgba(0,0,0,.06);
    line-height: 1.4;
  }
  /* Right-side wrapper for any combination of offer-tag / sibling-tag /
     outcome badge on a single row. */
  .ea-app__schools-tags {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  /* Offered-school row highlight. Tints the row background + adds a
     left accent matching the offer status (amber pending / green
     accepted / grey declined). */
  .ea-app__schools-row.is-offered {
    background: rgba(31,136,105,.10);
    border-left: 3px solid var(--st-ok-strip);
    margin: 0 -8px;
    padding: 4px 8px 4px 9px;
    border-radius: 4px;
  }
  .ea-app__schools-row.is-offered .ea-app__schools-name { font-weight: 700; }
  .ea-app__schools-row.is-offered-accepted {
    background: rgba(31,136,105,.10);
    border-left-color: var(--st-ok-strip);
  }
  .ea-app__schools-row.is-offered-declined {
    background: rgba(107,118,130,.10);
    border-left-color: var(--st-closed-strip);
  }
  /* Default for is-offered without a known status (pending or unknown) —
     use amber so it visually matches the action-needed card tone. */
  .ea-app__schools-row.is-offered:not(.is-offered-accepted):not(.is-offered-declined) {
    background: rgba(184,134,11,.10);
    border-left-color: var(--st-action-strip);
  }

  /* Offer status pill inside the schools list. */
  .offer-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    background: var(--st-ok-strip);
    color: #fff;
  }
  .offer-tag.is-pending  { background: var(--st-action-strip); }
  .offer-tag.is-accepted { background: var(--st-ok-strip); }
  .offer-tag.is-declined { background: var(--st-closed-strip); }

  .ea-app__inline-alert {
    background: rgba(184,134,11,.10);
    border: 1px dashed rgba(184,134,11,.45);
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 0.9375rem;
    color: #6B4A00;
  }
  .ea-app__inline-alert.is-cancel {
    background: rgba(107,118,130,.10);
    border-color: rgba(107,118,130,.4);
    color: var(--st-closed-fg);
  }
  .ea-app__inline-alert.is-offer {
    background: rgba(31,136,105,.10);
    border-color: rgba(31,136,105,.45);
    color: var(--st-ok-fg);
  }
  .ea-app__inline-alert.is-info {
    background: rgba(31,111,163,.10);
    border-color: rgba(31,111,163,.4);
    color: var(--st-info-fg);
  }
  .ea-app__inline-alert.is-new {
    background: #FAFBFC;
    border-color: var(--wn-line-strong);
    border-style: solid;
    color: var(--fg-2);
  }
  .ea-app__inline-alert b { font-weight: 600; }
  .ea-app__inline-alert .alert-ico { vertical-align: -3px; margin-right: 6px; }
  /* Reason block — appears inside declined-offer alerts, surfacing
     the free-text reason the parent supplied. Indented + boxed so
     it stands apart from the primary alert message but still reads
     as part of the same context. */
  .ea-app__inline-alert__reason {
    margin-top: 8px;
    padding: 8px 10px;
    background: rgba(0, 0, 0, .05);
    border-radius: 5px;
    font-size: 0.875rem;
    line-height: 1.45;
    white-space: pre-wrap; /* preserve any newlines the parent typed */
  }
  .ea-app__inline-alert__reason-label {
    font-weight: 600;
    color: var(--fg-2);
    margin-right: 4px;
  }

  .ea-app__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 2px;
  }

  .ea-app__side {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    text-align: right;
    gap: 12px;
  }
  .ea-app__top-side    { display: flex; flex-direction: column; gap: 8px; align-items: flex-end; }
  .ea-app__bottom-side {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 0.875rem;
    color: var(--fg-2);
    align-items: flex-end;
  }
  /* Each meta line stacks the label above its bold value so a long
     value (e.g. "16th October 2025") can wrap to its own line inside
     the fixed 220px side column rather than overflowing into the body. */
  .ea-app__bottom-side .meta-item {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1px;
    line-height: 1.35;
    max-width: 100%;
  }
  .ea-app__bottom-side .meta-label {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: var(--fg-3);
    white-space: nowrap;
  }
  .ea-app__bottom-side .meta-value {
    font-weight: 600;
    color: var(--fg-1);
    white-space: nowrap;
  }
  .ea-app__bottom-side .meta-item--warn .meta-label,
  .ea-app__bottom-side .meta-item--warn .meta-value {
    color: var(--st-action-fg);
  }
  .ea-app__bottom-side b { color: var(--fg-1); font-weight: 600; font-size: 0.9375rem; }

  /* Per-status surface tints */
  .ea-app[data-status="new"]      { background: var(--st-new-bg);     border-color: var(--st-new-border); }
  .ea-app[data-status="new"]      .ea-app__strip { background: var(--st-new-strip); }
  .ea-app[data-status="action"]   { background: var(--st-action-bg);  border-color: var(--st-action-border); }
  .ea-app[data-status="action"]   .ea-app__strip { background: var(--st-action-strip); }
  .ea-app[data-status="ok"]       { background: var(--st-ok-bg);      border-color: var(--st-ok-border); }
  .ea-app[data-status="ok"]       .ea-app__strip { background: var(--st-ok-strip); }
  .ea-app[data-status="closed"]   { background: var(--st-closed-bg);  border-color: var(--st-closed-border); }
  .ea-app[data-status="closed"]   .ea-app__strip { background: var(--st-closed-strip); }
  .ea-app[data-status="info"]     { background: var(--st-info-bg);    border-color: var(--st-info-border); }
  .ea-app[data-status="info"]     .ea-app__strip { background: var(--st-info-strip); }

  /* Status pill */
  .pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px 4px 8px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    background: #EDF0F3;
    color: var(--fg-1);
    border: 1px solid transparent;
    line-height: 1;
    height: 24px;
    white-space: nowrap;
  }
  .pill__dot { width: 8px; height: 8px; border-radius: 50%; background: currentColor; flex-shrink: 0; }
  .pill--new      { background: #fff;    color: var(--st-new-fg);    border-color: var(--st-new-border); }
  .pill--action   { background: #FBE6A8; color: var(--st-action-fg); border-color: #E8C766; }
  .pill--ok       { background: #C7E6D4; color: var(--st-ok-fg);     border-color: #8FCFA9; }
  .pill--closed   { background: #DDE3E9; color: var(--st-closed-fg); border-color: #BFC7CF; }
  .pill--info     { background: #C9E4F4; color: var(--st-info-fg);   border-color: #95C5E1; }

  /* Reference — label above, number below. The number is the most
     important thing the parent quotes when contacting the council, so
     it gets generous size and full contrast. */
  .ref {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1px;
    padding: 6px 10px;
    border-radius: 6px;
    background: rgba(0,0,0,.05);
    border: 1px solid rgba(0,0,0,.08);
  }
  .ref__label {
    font-family: var(--font-body);
    font-size: 10px;
    font-weight: 600;
    color: var(--fg-3);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    line-height: 1.2;
  }
  .ref__num {
    font-family: var(--font-mono);
    font-size: 15px;
    font-weight: 600;
    color: var(--fg-1);
    letter-spacing: 0.02em;
    line-height: 1.2;
  }

  /* ------------- Icons ------------- */
  .icon {
    width: 16px; height: 16px;
    stroke: currentColor;
    fill: none;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    flex-shrink: 0;
  }

  /* ------------- Additional Information + tile row -------------
     This section uses the existing Bulma classes (.tile, .notification,
     .columns, .title, .is-4-widescreen) and FontAwesome icons, so no
     additional CSS is needed here. */

  /* ============================================================
     RESPONSIVE
     - Tablet/narrow (≤900px): collapse the app card's right column,
       wrap the child header so action buttons drop to their own row.
     - Mobile (≤600px): tighten page padding, shrink the avatar,
       compact spacing inside cards.
     ============================================================ */
  @media (max-width: 900px) {
    /* On mobile, the side column splits in two: the pill + reference
       float up to the top of the card (so the parent sees status at
       a glance), the body sits in the middle, and the dates sit at
       the bottom. We use `display: contents` on .ea-app__side so its
       top-side / bottom-side children become direct grid items and
       can be placed in separate rows. */
    .ea-app {
      grid-template-columns: 4px 1fr;
      grid-template-rows: auto auto auto;
    }
    .ea-app__strip { grid-column: 1; grid-row: 1 / span 3; }

    .ea-app__side { display: contents; }

    .ea-app__top-side {
      grid-column: 2;
      grid-row: 1;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: space-between;
      align-items: center;
      gap: 8px 12px;
      width: 100%;
    }

    .ea-app__body { grid-column: 2; grid-row: 2; }

    .ea-app__bottom-side {
      grid-column: 2;
      grid-row: 3;
      align-items: flex-start;
      text-align: left;
      flex-direction: column;
      gap: 6px;
    }
    /* Meta-items go inline on mobile — the column is full-width so
       there's plenty of room for "Outcome response date 16/10/2025". */
    .ea-app__bottom-side .meta-item {
      flex-direction: row;
      align-items: center;
      gap: 6px;
    }

    /* Wrap the child header so Edit details / Remove child drop to a new row */
    .ea-child__header { flex-wrap: wrap; }
    .ea-child__actions {
      flex: 1 0 100%;
      margin-top: 8px;
      margin-left: 0;
      justify-content: flex-start;
    }
  }

  @media (max-width: 600px) {
    .ea-page { padding: 8px 14px 60px; }

    /* Smaller avatar so name/meta column has more room */
    .ea-child__avatar {
      width: 36px; height: 36px;
      font-size: 0.875rem;
      border-radius: 6px;
    }
    .ea-child__header { gap: 10px; }

    /* Tighten meta separators */
    .ea-child__meta { gap: 6px 12px; }
    .ea-child__meta span::before { margin-right: 12px; }

    /* Application card padding + inner gaps */
    .ea-app { padding: 14px 14px; gap: 12px; }
    .ea-app__body { gap: 8px; }
    .ea-app__sub { gap: 6px 12px; }
    .ea-app__sub span::before { margin-right: 12px; }

    /* Schools list density */
    .ea-app__schools { padding: 8px 10px; }
    .ea-app__schools-row { flex-wrap: wrap; gap: 6px 10px; }
    /* Tags drop below the school name on tiny screens */
    .ea-app__schools-tags { margin-left: 0; }

    /* Action buttons wrap and grow */
    .ea-app__actions { gap: 6px; }
    .ea-app__actions .btn--small { flex: 1 1 auto; justify-content: center; }

    /* Skeleton follows the same tightening */
    .ea-skeleton__card { padding: 14px; }
  }
</style>

<svelte:head>
  <title>Your Home Page | eAdmissions</title>
</svelte:head>

{#if !isLoading}
  <main class="ea-page">
    <h1 class="title" style="color: #70457a; text-align:center;">Your Home Page</h1>

    <!-- ============================================================
         PUSH NOTIFICATION / SMS OPT-IN BANNER
         Surfaces once per session (cookie-suppressed for 7 days on
         dismiss). Hides automatically once the browser permission is
         granted/denied or the user has opted out.

         Held back until `ready` as well: both conditions depend on
         $User, so if $User resolves after first paint the banner
         appears late and shoves the whole page down. Showing it with
         the rest of the content means one paint, not two.
         ============================================================ -->
    {#if ready && (showPushNotificationAlert || showSMSOptIn)}
      <section class="ea-optin" aria-label="Notification opt-in">
        <button
          type="button"
          class="ea-optin__close"
          aria-label="Dismiss notification alerts"
          on:click={() => (showOptoutConfirmModal = true)}>×</button>

        <h2 class="ea-optin__title">Get eAdmissions alerts</h2>
        <p class="ea-optin__intro">
          You can sign up to receive optional messages during the current admissions round.
          <a href="#" on:click|preventDefault={() => { showSMSOptinHelp = true; }}>
            What gets sent?
          </a>
        </p>

        <div class="ea-optin__cards">
          {#if showPushNotificationAlert}
            <div class="ea-optin__card">
              <div class="ea-optin__icon" aria-hidden="true">
                <i class="fas fa-bell"></i>
              </div>
              <div class="ea-optin__body">
                <p class="ea-optin__heading"><b>Push notifications</b></p>
                <p class="ea-optin__copy">Would you like to receive push notifications?</p>
                <div class="ea-optin__buttons">
                  <button class="btn btn--small btn--primary"   on:click={onYesPushClick}>Yes</button>
                  <button class="btn btn--small btn--secondary" on:click={onNoPushClick}>No</button>
                </div>
              </div>
            </div>
          {/if}

          {#if showSMSOptIn}
            <div class="ea-optin__card">
              <div class="ea-optin__icon" aria-hidden="true">
                <i class="fas fa-sms"></i>
              </div>
              <div class="ea-optin__body">
                <p class="ea-optin__heading"><b>Text messages</b></p>
                <p class="ea-optin__copy">Would you like to receive text messages?</p>
                <div class="ea-optin__buttons">
                  <button
                    class="btn btn--small btn--primary"
                    on:click={e => {
                      e.preventDefault();
                      if ($User && $User.isMobileValidated === "P") showMobileVerification = true;
                      else askSMSVerificationCode = true;
                    }}>Yes</button>
                  <button
                    class="btn btn--small btn--secondary {isSMSOptingOut ? 'is-loading' : ''}"
                    disabled={isSMSOptingOut}
                    on:click={optOutSMS}>No</button>
                </div>
              </div>
            </div>
          {/if}
        </div>
      </section>
    {/if}

    <Help helpId={"UserHomeIntro"} showMainHeader={false} showContentHeader={true} showContent={true} />


    <!-- Top CTA — static, safe to paint immediately. -->
    <section class="ea-hero">
      <div class="ea-actions">
        <a
          class="btn btn--primary"
          href="#"
          on:click|preventDefault={() => dispatch('pageChange', '/application/childdetails/0')}>
          <span>Start an application</span>
          <span class="arrow">→</span>
        </a>
      </div>
    </section>

    <!-- Action-needed banner: aggregate of all apps that need parent input.
         Derived from the store, so it waits for `ready` — otherwise it
         can announce a stale count and then correct itself. -->
    {#if ready && actionApps.length > 0}
      {@const nextClosing = actionApps.find(a => a.closing)?.closing}
      <div class="ea-action-banner" role="status">
        <span class="ea-action-banner__icon" aria-hidden="true">!</span>
        <div class="ea-action-banner__body">
          <p class="ea-action-banner__title">
            {actionApps.length} application{actionApps.length === 1 ? '' : 's'} need{actionApps.length === 1 ? 's' : ''} your attention
          </p>
          {#if actionApps.length === 1}
            {@const a = actionApps[0]}
            <p class="ea-action-banner__msg">
              <b>{actionVerb(a)} {a.child.forename}'s</b> application{#if nextClosing}.{' '}Closing date is <b>{nextClosing}</b>{/if}.
            </p>
          {:else}
            <ul class="ea-action-banner__list">
              {#each actionApps as a}
                <li><b>{actionVerb(a)} {a.child.forename}'s</b> application</li>
              {/each}
            </ul>
            {#if nextClosing}
              <p class="ea-action-banner__msg ea-action-banner__msg--footer">
                Next closing date is <b>{nextClosing}</b>.
              </p>
            {/if}
          {/if}
        </div>
      </div>
    {/if}

    <!-- Section heading. The heading itself is static; the counts and
         sort control are store-derived, so they wait for `ready`. -->
    <div class="ea-section-title">
      <h2>Your children</h2>
      {#if ready}
        <div class="ea-section-tools">
          {#if isRefreshing}
            <span class="ea-refreshing" role="status">
              <svg viewBox="0 0 24 24" aria-hidden="true">{@html ICONS.refresh}</svg>
              Updating
            </span>
          {/if}
          <span class="ea-section-meta">
            {displayChildren.length} child{displayChildren.length === 1 ? '' : 'ren'} · {totalApps} application{totalApps === 1 ? '' : 's'}
          </span>
          {#if displayChildren.length > 2}
            <label class="ea-sort">
              <span class="ea-sort__label">Sort by</span>
              <select class="ea-sort__select" bind:value={sortBy}>
                <option value="actionFirst">Action needed first</option>
                <option value="name">Name (A–Z)</option>
                <option value="ageDesc">Oldest first</option>
                <option value="ageAsc">Youngest first</option>
              </select>
            </label>
          {/if}
        </div>
      {/if}
    </div>

    <!-- ============================================================
         CHILDREN — one of three branches: skeleton, error, or content.
         Nothing store-derived renders outside the `ready` branch, so a
         status can never flick from one value to another after paint.
         ============================================================ -->
    {#if loadState === "loading"}
      <div class="ea-skeleton" aria-busy="true" aria-live="polite" aria-label="Loading your children">
        {#each [0, 1] as i}
          <div class="ea-skeleton__child">
            <div class="ea-skeleton__header">
              <div class="ea-bone" style="width:42px;height:42px;border-radius:8px;"></div>
              <div class="ea-skeleton__header-text">
                <div class="ea-bone" style="width:180px;height:18px;"></div>
                <div class="ea-bone" style="width:240px;height:13px;"></div>
              </div>
            </div>
            <div class="ea-skeleton__card">
              <div class="ea-skeleton__row">
                <div class="ea-bone" style="width:260px;height:18px;"></div>
                <div class="ea-bone" style="width:120px;height:24px;border-radius:999px;margin-left:auto;"></div>
              </div>
              <div class="ea-bone" style="width:160px;height:13px;"></div>
              <div class="ea-bone" style="width:100%;height:64px;border-radius:8px;"></div>
              <div class="ea-skeleton__row">
                <div class="ea-bone" style="width:150px;height:32px;"></div>
                <div class="ea-bone" style="width:130px;height:32px;"></div>
              </div>
            </div>
          </div>
        {/each}
      </div>

    {:else if loadState === "error"}
      <div class="ea-error" role="alert">
        <span class="ea-error__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            {@html ICONS.warn}
          </svg>
        </span>
        <div class="ea-error__body">
          <p class="ea-error__title">Your children and applications didn't load</p>
          <p class="ea-error__msg">
            The connection to eAdmissions failed. Nothing has changed — your
            applications are safe. Try again, and if it keeps failing, contact
            your Local Authority.
          </p>
          <button class="btn btn--small btn--primary" type="button" on:click={retryLoad}>
            <svg class="icon" viewBox="0 0 24 24">{@html ICONS.refresh}</svg>
            <span>Try again</span>
          </button>
        </div>
      </div>

    {:else}
      <!-- Per-child blocks -->
      {#each sortedChildren as child (child.id)}
        {@const hasSubmitted = childHasSubmitted(child)}
        {@const hasSchools = child.applications.some(app =>
          (app.schools || []).some(s => s && s.name)
        )}
        {@const siblings = child.siblings || []}
        {@const linkedRefs = siblingLinks.get(child.id) || []}
        {@const linkedAsSibling = child.isSibling || linkedRefs.length > 0}
        {@const removeDisabled = hasSubmitted || linkedAsSibling}
        {@const hasOffer = child.applications.some(a => a.offerStatus)}
        {@const removeTitle =
          hasSubmitted
            ? "You can't remove a child while a submitted application exists. Cancel the application first."
          : linkedAsSibling
            ? (linkedRefs.length > 0
                ? `Linked as a sibling on ${linkedRefs.map(r => `${r.byChildName}'s application (${r.schoolName})`).join(', ')}. Remove the sibling reference from those preferences first.`
                : `This child is recorded as a sibling on another application. Remove the sibling reference from that preference first.`)
            : ''}

        <div class="ea-child">
          <div class="ea-child__header">
            <div class="ea-child__avatar">{initials(child.name)}</div>
            <div class="ea-child__main">
              <div class="ea-child__name">
                {child.name}
                <span class="ea-child__name-sex">({child.sex})</span>
              </div>
              <div class="ea-child__meta">
                <span>DOB {child.dob}</span>
                {#if child.postcode}<span>Postcode {child.postcode}</span>{/if}
              </div>
              {#if child.ehcpNotAllowed}
                <div class="ehcp-warning" role="alert">
                  <span class="ehcp-warning__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      {@html ICONS.warn}
                    </svg>
                  </span>
                  <span class="ehcp-warning__text">{child.ehcpText}</span>
                </div>
              {/if}
              {#if siblings.length === 1}
                <div class="sibling-callout">
                  <span class="sibling-callout__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      {@html ICONS.sibling}
                    </svg>
                  </span>
                  <span class="sibling-callout__text">
                    <b>Sibling link recorded.</b>
                    {siblings[0].siblingName}{#if siblings[0].siblingSchool}, who attends <b>{siblings[0].siblingSchool}</b>,{/if}
                    is claimed as a sibling{#if siblings[0].school}{' '}on the application to <b>{siblings[0].school}</b>{/if}.
                    This is included on the application.
                  </span>
                </div>
              {:else if siblings.length > 1}
                <div class="sibling-callout">
                  <span class="sibling-callout__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      {@html ICONS.sibling}
                    </svg>
                  </span>
                  <span class="sibling-callout__text">
                    <b>Sibling links recorded.</b>
                    {child.forename}'s application claims sibling priority:
                    <ul class="sibling-list">
                      {#each siblings as s}
                        <li>{s.siblingName}{#if s.siblingSchool}{' '}(attends <b>{s.siblingSchool}</b>){/if}{#if s.school}{' '}&mdash; claimed on <b>{s.school}</b>{/if}</li>
                      {/each}
                    </ul>
                    These are included on the application.
                  </span>
                </div>
              {/if}
              {#if linkedAsSibling}
                <div class="sibling-callout is-incoming">
                  <span class="sibling-callout__icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      {@html ICONS.sibling}
                    </svg>
                  </span>
                  <span class="sibling-callout__text">
                    <b>Linked as a sibling.</b>
                    {#if linkedRefs.length > 0}
                      {child.forename} is recorded as a sibling on
                      {#each linkedRefs as r, i}{#if i > 0}{i === linkedRefs.length - 1 ? ' and ' : ', '}{/if}<b>{r.byChildName}'s</b> application ({r.schoolName}){/each}.
                      To remove {child.forename} from your account, edit those applications first and remove the sibling reference.
                    {:else}
                      {child.forename} is recorded as a sibling on another application.
                      To remove {child.forename} from your account, edit that application first and remove the sibling reference.
                    {/if}
                  </span>
                </div>
              {/if}
            </div>
            <div class="ea-child__actions">
              <!-- Once any application has an offer (pending, accepted,
                   declined or responded) the child's details can no longer
                   be changed online, so there is no Edit details button. -->
              {#if !hasOffer}
                <a
                  class="btn btn--ghost btn--small"
                  href="/application/childsummary/{child.id}/{child.applications.find(a => a.id > 0 && a.state !== 'cancel' && a.state !== 'deleted')?.id || ''}">
                  <svg class="icon" viewBox="0 0 24 24">{@html ICONS.pencil}</svg>
                  Edit details
                </a>
              {/if}
              {#if !hasSchools}
                <button
                  class="btn btn--ghost btn--small"
                  type="button"
                  disabled={removeDisabled}
                  title={removeTitle}
                  on:click={() => requestDeleteChild(child)}>
                  <svg class="icon" viewBox="0 0 24 24">{@html ICONS.trash}</svg>
                  Remove child
                </button>
              {/if}
            </div>
          </div>

          <!-- One AppCard per application.
               When a child is EHCP-ineligible (isEHCPAllowed = 'N') the
               back end returns no applications row for them and my
               mapper synthesises a placeholder (appId 0) so the child
               still gets a card. For an ineligible child the placeholder
               would show an "Add Schools" CTA — which is exactly what
               the LA is refusing to allow. Skip the placeholder in
               that case; the red EHCP warning under the child header
               already explains what to do (contact the LA directly).
               Real applications (appId > 0) still render normally so
               existing records can be viewed even after eligibility
               changes. -->
          {#each child.applications as app (app.id)}
            {#if !(app.id === 0 && child.ehcpNotAllowed)}
            {@const offerOverride = app.offerStatus ? OFFER_STATES[app.offerStatus] : null}
            {@const tone      = offerOverride?.tone || STATES[app.state]?.tone || 'closed'}
            {@const pillLabel = offerOverride?.pill || STATES[app.state]?.pill || app.state}

            <div class="ea-app" data-status={tone}>
              <div class="ea-app__strip" aria-hidden="true"></div>

              <div class="ea-app__body">
                <div class="ea-app__head">
                  <h3 class="ea-app__title">{app.title}</h3>
                </div>

                <div class="ea-app__sub">
                  {#if app.schools?.length > 0}
                    <span>{app.schools.length} school{app.schools.length === 1 ? '' : 's'} selected</span>
                  {:else if app.state === 'notSub'}
                    <span>No preferences yet</span>
                  {/if}
                  {#if app.phase}<span>{app.phase}</span>{/if}
                </div>

                {#if app.schools?.length > 0}
                  <div class="ea-app__schools">
                    {#each app.schools as s (s.p)}
                      {@const isOffered = !!(app.offerSchool && s.name === app.offerSchool)}
                      <div
                        class="ea-app__schools-row"
                        class:is-offered={isOffered}
                        class:is-offered-accepted={isOffered && app.offerStatus === 'accepted'}
                        class:is-offered-declined={isOffered && app.offerStatus === 'declined'}>
                        <span class="ea-app__schools-pref">{s.p}</span>
                        <span class="ea-app__schools-name">{s.name}</span>
                        {#if s.schoolGender && s.schoolGender !== 'Mixed' && s.schoolGender !== 'M'}
                          <span class="ea-app__schools-gender" title="School intake">{s.schoolGender}</span>
                        {/if}
                        <span class="ea-app__schools-tags">
                          {#if isOffered}
                            <span
                              class="offer-tag"
                              class:is-pending={!app.offerStatus || app.offerStatus === 'pending'}
                              class:is-accepted={app.offerStatus === 'accepted'}
                              class:is-declined={app.offerStatus === 'declined'}
                              title="Place offered at this school">
                              {#if app.offerStatus === 'accepted'}✓ Offer accepted
                              {:else if app.offerStatus === 'declined'}Offer declined
                              {:else if app.offerStatus === 'responded'}✓ Offer responded
                              {:else}● Offered
                              {/if}
                            </span>
                          {/if}
                          {#if s.siblingName}
                            <span class="sibling-tag" title="Sibling declared at this school">
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                                {@html ICONS.sibling}
                              </svg>
                              Sibling · {s.siblingName}
                            </span>
                          {/if}
                          {#if s.outcome && !isOffered}
                            <span
                              class="ea-app__schools-meta"
                              style="font-weight: 600; color: {s.outcome === 'Offered' ? 'var(--st-ok-fg)' : 'var(--st-action-fg)'};">
                              {s.outcome}
                            </span>
                          {/if}
                        </span>
                      </div>
                    {/each}
                  </div>
                {/if}

                <!-- Inline alert per state. Offer-respond is an overlay on
                     a Submitted card (not a separate state). -->
                {#if app.state === 'edits'}
                  <div class="ea-app__inline-alert">
                    <svg class="icon alert-ico" viewBox="0 0 24 24">{@html ICONS.warn}</svg>
                    <b>Changes saved but not submitted.</b>
                    {#if app.edits?.at}You changed your school preferences on {app.edits.at}.{/if}
                    Re-submit before the closing date for the changes to count.
                  </div>
                {:else if app.state === 'cancel'}
                  <div class="ea-app__inline-alert is-cancel">
                    <svg class="icon alert-ico" viewBox="0 0 24 24">{@html ICONS.ban}</svg>
                    <b>{app.cancelledAt ? `Cancelled by the Local Authority on ${app.cancelledAt}.` : "Cancelled by the Local Authority."}</b>
                    {#if app.cancelReason}Reason: {app.cancelReason}{/if}
                  </div>
                {:else if app.state === 'deleted'}
                  <div class="ea-app__inline-alert is-cancel">
                    <svg class="icon alert-ico" viewBox="0 0 24 24">{@html ICONS.trash}</svg>
                    <b>{app.deletedAt ? `Application deleted on ${app.deletedAt}.` : "Application deleted."}</b>
                  </div>
                {:else if app.offerStatus === 'pending'}
                  <div class="ea-app__inline-alert">
                    <svg class="icon alert-ico" viewBox="0 0 24 24">{@html ICONS.warn}</svg>
                    <b>{app.offerSchool ? `Place offered at ${app.offerSchool}.` : "Place offered."}</b>
                    {#if app.respondBy}Respond by {app.respondBy} to accept or decline.{/if}
                  </div>
                {:else if app.offerStatus === 'none'}
                  <!-- Outcome published but no school offered — nothing to
                       accept or decline, so no "Place offered" wording. -->
                  <div class="ea-app__inline-alert">
                    <svg class="icon alert-ico" viewBox="0 0 24 24">{@html ICONS.info}</svg>
                    <b>No offer at this time.</b>
                  </div>
                {:else if app.offerStatus === 'notReceived'}
                  <!-- Late application: outcome shown but the LA hasn't
                       loaded an offer yet. View full outcome shows the
                       "OfferNotReceived" help text. -->
                  <div class="ea-app__inline-alert is-info">
                    <svg class="icon alert-ico" viewBox="0 0 24 24">{@html ICONS.info}</svg>
                    <b>Outcome not yet available.</b>
                  </div>
                {:else if app.offerStatus === 'accepted'}
                  <div class="ea-app__inline-alert is-offer">
                    <svg class="icon alert-ico" viewBox="0 0 24 24">{@html ICONS.check}</svg>
                    <b>{app.offerSchool ? `Offer accepted for ${app.offerSchool}.` : "Offer accepted."}</b>
                    {#if app.offerReplied}You responded on {app.offerReplied}.{/if}
                  </div>
                {:else if app.offerStatus === 'declined'}
                  <div class="ea-app__inline-alert is-cancel">
                    <svg class="icon alert-ico" viewBox="0 0 24 24">{@html ICONS.ban}</svg>
                    <b>{app.offerSchool ? `Offer declined for ${app.offerSchool}.` : "Offer declined."}</b>
                    {#if app.offerReplied}You responded on {app.offerReplied}.{/if}
                    {#if app.offerDeclinedReason}
                      <div class="ea-app__inline-alert__reason">
                        <span class="ea-app__inline-alert__reason-label">Reason:</span>
                        {app.offerDeclinedReason}
                      </div>
                    {/if}
                  </div>
                {:else if app.offerStatus === 'responded'}
                  <div class="ea-app__inline-alert is-offer">
                    <svg class="icon alert-ico" viewBox="0 0 24 24">{@html ICONS.check}</svg>
                    <b>Offer responded.</b>
                    {#if app.offerReplied}You responded on {app.offerReplied}.{/if}
                  </div>
                {/if}

                <!-- Actions row -->
                <div class="ea-app__actions">
                  {#each app.actions as kind}
                    {@const def = ACTION_DEFS[kind] || { label: kind, primary: false, icon: 'arrow' }}
                    <!-- Two delete variants:
                         - `delete` (cancelled app) → inline API call.
                           Renders as a real <button> so we can trigger
                           the confirm modal without a page navigation.
                         - `delete-draft` (Not Submitted app) → routes to
                           the existing /application/delete/{cid}/{aid}
                           page which has richer confirmation + kicks
                           off the server-side delete itself.
                         Only the inline flavour is gated on `deletable`;
                         delete-draft always navigates. -->
                    {@const isDeleteInline = kind === 'delete'}
                    {@const disabled = isDeleteInline && !app.deletable}
                    <!-- The attach-docs button label shifts based on whether
                         the parent has already uploaded anything. Fresh app
                         → "Attach a document"; app with N docs → "Manage
                         documents (N)". The URL destination is the same
                         page (which lists existing + accepts new). -->
                    {@const isAttachWithDocs = kind === 'attach-docs' && app.attachedDocumentCount > 0}
                    {@const label = isAttachWithDocs
                      ? `Manage documents (${app.attachedDocumentCount})`
                      : def.label}
                    {#if isDeleteInline}
                      <button
                        type="button"
                        class="btn btn--small btn--secondary"
                        disabled={disabled}
                        title={disabled ? app.deleteReason : ''}
                        on:click={() => requestDeleteApplication(child, app)}>
                        <svg class="icon" viewBox="0 0 24 24">{@html ICONS[def.icon] || ''}</svg>
                        <span>{label}</span>
                      </button>
                    {:else}
                      <a
                        href={disabled ? '#' : appUrl(child, app, kind)}
                        class="btn btn--small {def.primary ? 'btn--primary' : 'btn--secondary'}"
                        class:disabled
                        aria-disabled={disabled}
                        title={disabled ? app.deleteReason : ''}
                        on:click={e => { if (disabled) e.preventDefault(); }}>
                        <svg class="icon" viewBox="0 0 24 24">{@html ICONS[def.icon] || ''}</svg>
                        <span>{label}</span>
                        {#if def.primary}<span class="arrow">→</span>{/if}
                      </a>
                    {/if}
                  {/each}
                </div>
              </div>

              <!-- Right-hand status column -->
              <div class="ea-app__side">
                <div class="ea-app__top-side">
                  {#if app.offerStatus !== 'none' && app.offerStatus !== 'notReceived'}
                    <span class="pill pill--{tone}">
                      <span class="pill__dot" aria-hidden="true"></span>
                      {pillLabel}
                    </span>
                  {/if}
                  {#if app.reference}
                    <span class="ref">
                      <span class="ref__label">Reference</span>
                      <span class="ref__num">{app.reference}</span>
                    </span>
                  {/if}
                </div>

                <div class="ea-app__bottom-side">
                  {#if app.state === 'notSub'}
                    {#if app.closing}
                      <span class="meta-item">
                        <span class="meta-label">
                          <svg class="icon alert-ico" viewBox="0 0 24 24">{@html ICONS.calendar}</svg>
                          Closes
                        </span>
                        <b class="meta-value">{app.closing}</b>
                      </span>
                    {/if}
                    <span class="meta-item">
                      <span class="meta-label">Last submitted</span>
                      <b class="meta-value">never</b>
                    </span>
                  {:else if app.state === 'sub'}
                    <span class="meta-item" class:meta-item--warn={app.submittedLate}>
                      <span class="meta-label">
                        <svg class="icon alert-ico" viewBox="0 0 24 24">{@html ICONS.check}</svg>
                        {app.submittedLate ? 'Submitted after closing' : 'Submitted'}
                      </span>
                      <b class="meta-value">{app.submitted}</b>
                    </span>
                    {#if app.offerReplied}
                      <span class="meta-item">
                        <span class="meta-label">
                          <svg class="icon alert-ico" viewBox="0 0 24 24">{@html ICONS.check}</svg>
                          Outcome response date
                        </span>
                        <b class="meta-value">{app.offerReplied}</b>
                      </span>
                    {:else if app.respondBy}
                      <span class="meta-item">
                        <span class="meta-label">
                          <svg class="icon alert-ico" viewBox="0 0 24 24">{@html ICONS.calendar}</svg>
                          Respond by
                        </span>
                        <b class="meta-value">{app.respondBy}</b>
                      </span>
                    {/if}
                  {:else if app.state === 'edits'}
                    <span class="meta-item">
                      <span class="meta-label">Last submitted</span>
                      <b class="meta-value">{app.submitted}</b>
                    </span>
                    {#if app.edits?.at}
                      <span class="meta-item">
                        <span class="meta-label">Edits saved</span>
                        <b class="meta-value">{app.edits.at}</b>
                      </span>
                    {/if}
                    {#if app.closing}
                      <span class="meta-item">
                        <span class="meta-label">Closes</span>
                        <b class="meta-value">{app.closing}</b>
                      </span>
                    {/if}
                  {:else if app.state === 'cancel'}
                    {#if app.submitted}
                      <span class="meta-item">
                        <span class="meta-label">Originally submitted</span>
                        <b class="meta-value">{app.submitted}</b>
                      </span>
                    {/if}
                    {#if app.cancelledAt}
                      <span class="meta-item">
                        <span class="meta-label">
                          <svg class="icon alert-ico" viewBox="0 0 24 24">{@html ICONS.ban}</svg>
                          Cancelled
                        </span>
                        <b class="meta-value">{app.cancelledAt}</b>
                      </span>
                    {/if}
                  {:else if app.state === 'deleted'}
                    {#if app.submitted}
                      <span class="meta-item">
                        <span class="meta-label">Originally submitted</span>
                        <b class="meta-value">{app.submitted}</b>
                      </span>
                    {/if}
                    {#if app.deletedAt}
                      <span class="meta-item">
                        <span class="meta-label">
                          <svg class="icon alert-ico" viewBox="0 0 24 24">{@html ICONS.trash}</svg>
                          Deleted
                        </span>
                        <b class="meta-value">{app.deletedAt}</b>
                      </span>
                    {/if}
                  {/if}
                </div>
              </div>
            </div>
            {/if}
          {/each}
        </div>
      {:else}
        <div class="ea-empty">
          <p>You haven't added any children yet.</p>
          <p>Click <b>Start an application</b> above to get started.</p>
        </div>
      {/each}
    {/if}

    <!-- Additional Information + bottom navigation tiles
         Restored to the original Bulma + FontAwesome markup so this
         section matches the rest of the existing eAdmissions site. -->
    <div class="tile is-parent" style="padding:0rem;margin-top:1.5rem;">
      <article
        aria-label=""
        class="tile is-child notification"
        style="background-color: transparent;padding-top:0px;padding-bottom:12px;">
        <h2 style="color: #70457a;"><strong>Additional Information</strong></h2>
        <hr style="margin-top:0px;"/>
        <article class="notification">
          {#if additionalInformation}
            {@html additionalInformation}
          {:else}
            <p>
              Click on <b>'Start an application'</b> to make a school application for September 2026 for your child.
              This should be repeated if you need to make a school application for another child.
            </p>
            <p>
              If you require a school place for your child now (In Year) please contact your home Local Authority.
            </p>
            <p>
              You can make changes to your submitted application. Just return to this page (Your Home Page) and click on:
            </p>
            <ul>
              <li><b>'Edit child's details'</b> to change your child's information, at any time.</li>
              <li><b>'Edit selected schools'</b> to change the schools you have selected (before the closing date).</li>
            </ul>
            <p>Supporting documentation can only be added once your application has been submitted.</p>
          {/if}
        </article>
      </article>
    </div>

    <div class="tile is-parent" style="padding-bottom:0px;padding-top:0px;">
      <div
        tabindex="0"
        aria-label="Go to my account"
        class="tile tile-hover is-parent column is-4-widescreen"
        on:click={() => dispatch('pageChange', '/useraccount/details')}
        on:keypress={e => {
          if (e.keyCode == 13) dispatch('pageChange', '/useraccount/details');
        }}>
        <article
          class="tile home-button is-child notification is-primary"
          style="background-color: #6F4077;">
          <div class="columns is-mobile">
            <div class="column is-10">
              <p class="title is-4">My Account</p>
              <p style="text-decoration:underline">View</p>
            </div>
            <div class="column is-2">
              <i class="fas fa-user" style="font-size:2.5rem;"></i>
            </div>
          </div>
        </article>
      </div>
      <div
        tabindex="0"
        aria-label="Go to find schools"
        class="tile tile-hover is-parent column is-4-widescreen"
        on:click={() => dispatch('pageChange', '/findschools')}
        on:keypress={e => {
          if (e.keyCode == 13) dispatch('pageChange', '/findschools');
        }}>
        <article
          class="tile home-button is-child notification is-primary"
          style="background-color:#416EA1">
          <div class="columns is-mobile">
            <div class="column is-10">
              <p class="title is-4">Find schools</p>
              <p style="text-decoration:underline">View</p>
            </div>
            <div class="column is-2">
              <i class="fas fa-map-marker-alt" style="font-size:2.5rem;"></i>
            </div>
          </div>
        </article>
      </div>
      <div
        tabindex="0"
        aria-label="Go to help and tutorials"
        class="tile tile-hover is-parent column is-4-widescreen"
        on:click={() => dispatch('pageChange', '/help')}
        on:keypress={e => {
          if (e.keyCode == 13) dispatch('pageChange', '/help');
        }}>
        <article
          class="tile home-button is-child notification is-primary"
          style="background-color: #666666;">
          <div class="columns is-mobile">
            <div class="column is-10">
              <p class="title is-4">Help &amp; Tutorials</p>
              <p style="text-decoration:underline">View</p>
            </div>
            <div class="column is-2">
              <i class="fas fa-question-circle" style="font-size:2.5rem;"></i>
            </div>
          </div>
        </article>
      </div>
    </div>
  </main>
{/if}

<!-- ============================================================
     MODALS — kept from the original component so existing
     flows (delete child, email validation, feedback, push +
     SMS opt-in) continue to work unchanged.
     ============================================================ -->
<ModalConfirm
  showModal={childToDelete}
  on:ok={deleteChild}
  bind:isUpdating={isChildDeleting}>
  Are you sure you want to delete this child?
</ModalConfirm>

<ModalConfirm
  showModal={applicationToDelete}
  on:ok={deleteApplication}
  bind:isUpdating={isApplicationDeleting}>
  Are you sure you want to delete this application? This can't be undone.
</ModalConfirm>

<ModalConfirm
  showModal={showOptoutConfirmModal}
  on:ok={optoutNotificationAlerts}
  bind:isUpdating={isOptingOut}>
  Are you sure you want to opt out from text message and/or push notification alerts?
</ModalConfirm>

<Modal bind:showModal={emailvalidated}>
  <p>Your email address has now been validated successfully.</p>
</Modal>

<Modal bind:showModal={askSMSVerificationCode}>
  <p style="padding:0.5rem;margin-bottom:0;">
    Please click 'Send' to send mobile verification code to the mobile number:
    <b>{$User && $User.mobile}</b>
  </p>
  <p style="padding:0.5rem;margin-bottom:0;">
    If the above mobile number is not correct, please
    <b><a href="useraccount/editaccount">click here</a></b>
    to update your account.
  </p>

  <footer class="modal-card-foot buttons" slot="footer" style="justify-content: space-between;">
    <button
      class="button continue"
      on:click={() => { askSMSVerificationCode = false; }}>
      Close
    </button>
    <button
      class="button is-primary continue {isSMSOptingOut ? 'is-loading' : ''}"
      disabled={isSendingSMSVerificationCode}
      on:click={sendSMSVerificationCode}>
      Send
    </button>
  </footer>
</Modal>

<VerifySMS
  bind:showModal={showMobileVerification}
  mobile={$User && $User.mobile}
  isDispatchResend={true}
  on:resend={() => { showMobileVerification = false; askSMSVerificationCode = true; }}
  on:close={() => (showMobileVerification = false)} />

<Modal bind:showModal={showSMSOptinHelp}>
  <Help
    helpId="SmsTrial"
    showSearchOption={false}
    showContent={true}
    doAnimation={false}
    isPopup={true} />
</Modal>

<Feedback bind:showModal={showFeedback} />